import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronDown, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { SITE_NAMES, SITE_COLORS, enrollmentAPI, contactAPI, themeAPI, analyticsAPI } from '../../lib/api';
import { AccessibilityButton } from './AccessibilityButton';
import { SeasonalDecorations } from './SeasonalDecorations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const getNavigation = (siteKey, enrollmentActive) => {
  const nav = [
    { name: 'Úvod', href: `/${siteKey}` },
    { name: 'O nás', href: `/${siteKey}/o-nas` },
    { name: 'Aktuality', href: `/${siteKey}/aktuality` },
    { name: 'Třídy', href: `/${siteKey}/tridy` },
    { name: 'Galerie', href: `/${siteKey}/galerie` },
    { name: 'Jídelníček', href: `/${siteKey}/jidelnicek` },
    { 
      name: 'Dokumenty', 
      href: `/${siteKey}/dokumenty`,
      children: [
        { name: 'Úřední deska', href: `/${siteKey}/dokumenty?category=uredni_deska` },
        { name: 'Pro rodiče', href: `/${siteKey}/pro-rodice` },
        { name: 'Formuláře', href: `/${siteKey}/dokumenty?category=formulare` }
      ]
    },
    { name: 'Kalendář', href: `/${siteKey}/kalendar` },
    { name: 'Náš tým', href: `/${siteKey}/nas-tym` },
    { name: 'Kontakt', href: `/${siteKey}/kontakt` }
  ];

  if (enrollmentActive) {
    nav.splice(2, 0, { name: 'Zápis', href: `/${siteKey}/zapis`, highlight: true });
  }

  return nav;
};

export const PublicLayout = ({ children, siteKey }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [enrollmentActive, setEnrollmentActive] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [theme, setTheme] = useState({ theme: 'none', enabled: true });
  const location = useLocation();
  const siteName = SITE_NAMES[siteKey];
  const colors = SITE_COLORS[siteKey];

  useEffect(() => {
    enrollmentAPI.get(siteKey).then(res => {
      setEnrollmentActive(res.data?.is_active || false);
    }).catch(() => {});
    contactAPI.getInfo(siteKey).then(res => {
      setContactInfo(res.data || null);
    }).catch(() => {});
    themeAPI.get().then(res => setTheme(res.data || { theme: 'none', enabled: true })).catch(() => {});
  }, [siteKey]);

  // Tracking návštěvy
  useEffect(() => {
    analyticsAPI.track(location.pathname, siteKey);
  }, [location.pathname, siteKey]);

  const navigation = getNavigation(siteKey, enrollmentActive);

  const isActive = (href) => {
    if (href === `/${siteKey}`) return location.pathname === `/${siteKey}`;
    return location.pathname.startsWith(href.split('?')[0]);
  };

  const logoEmoji = siteKey === 'ms' ? '🏫' : '🏡';
  const primaryColor = colors.primary;

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      {theme.enabled && <SeasonalDecorations theme={theme.theme} />}      {/* Top Bar */}
      <div 
        className="py-2.5 text-sm text-white"
        style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-4">
            {contactInfo?.phone && (
              <a href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} className="flex items-center gap-1.5 hover:opacity-80 transition-opacity" data-testid="topbar-phone">
                <Phone size={14} />
                <span className="hidden sm:inline">{contactInfo.phone}</span>
              </a>
            )}
            {contactInfo?.email && (
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-1.5 hover:opacity-80 transition-opacity" data-testid="topbar-email">
                <Mail size={14} />
                <span className="hidden sm:inline">{contactInfo.email}</span>
              </a>
            )}
            <AccessibilityButton />
          </div>
          <Link to="/" className="flex items-center gap-1.5 hover:opacity-80 transition-opacity">
            <Home size={14} />
            <span className="hidden sm:inline">Rozcestník</span>
          </Link>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b-4 border-[#F0F4F8] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <Link to={`/${siteKey}`} className="flex items-center gap-3" data-testid="logo-link">
              <div 
                className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl flex items-center justify-center text-2xl shadow-lg"
                style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
              >
                {logoEmoji}
              </div>
              <div className="hidden sm:block">
                <div className="font-bold text-[#2D3748] text-lg">{siteName}</div>
                <div className="text-xs text-[#64748B]">
                  {siteKey === 'ms' ? 'Mateřská škola' : 'Dětská skupina'}
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => (
                item.children ? (
                  <DropdownMenu key={item.name}>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className={`nav-link px-3 py-2 text-sm flex items-center gap-1 ${isActive(item.href) ? 'active' : ''}`}
                        style={isActive(item.href) ? { color: primaryColor, background: `${primaryColor}15` } : {}}
                      >
                        {item.name}
                        <ChevronDown size={14} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      {item.children.map((child) => (
                        <DropdownMenuItem key={child.href} asChild>
                          <Link to={child.href} className="cursor-pointer">
                            {child.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`nav-link px-3 py-2 text-sm ${isActive(item.href) ? 'active' : ''} ${item.highlight ? 'font-bold' : ''}`}
                    style={
                      item.highlight && !isActive(item.href) 
                        ? { color: '#FF6B6B', background: '#FF6B6B15' }
                        : isActive(item.href) 
                          ? { color: primaryColor, background: `${primaryColor}15` } 
                          : {}
                    }
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-xl hover:bg-[#F0F4F8]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t-2 border-[#F0F4F8] py-4">
            <div className="max-w-7xl mx-auto px-4 space-y-1">
              {navigation.map((item) => (
                <React.Fragment key={item.name}>
                  <Link
                    to={item.href.split('?')[0]}
                    className={`block px-4 py-3 rounded-xl text-sm font-semibold ${
                      isActive(item.href) ? 'text-white' : 'text-[#64748B] hover:bg-[#F0F4F8]'
                    }`}
                    style={isActive(item.href) ? { background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` } : {}}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  {item.children && (
                    <div className="pl-4">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-[#64748B] hover:text-[#45AAF2]"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="footer mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* About */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-xl"
                  style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                >
                  {logoEmoji}
                </div>
                <div>
                  <div className="font-bold text-white">{siteName}</div>
                  <div className="text-sm text-[#FECA57]">
                    {siteKey === 'ms' ? 'Mateřská škola' : 'Dětská skupina'}
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">
                {siteKey === 'ms' 
                  ? 'Moderní mateřská škola zaměřená na všestranný rozvoj dětí.' 
                  : 'Rodinná dětská skupina pro nejmenší v přátelském prostředí.'}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-bold mb-4 text-[#FECA57]">Rychlé odkazy</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to={`/${siteKey}/o-nas`}>O nás</Link></li>
                <li><Link to={`/${siteKey}/aktuality`}>Aktuality</Link></li>
                <li><Link to={`/${siteKey}/tridy`}>Naše třídy</Link></li>
                <li><Link to={`/${siteKey}/galerie`}>Fotogalerie</Link></li>
              </ul>
            </div>

            {/* Documents */}
            <div>
              <h3 className="font-bold mb-4 text-[#FECA57]">Dokumenty</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to={`/${siteKey}/dokumenty?category=uredni_deska`}>Úřední deska</Link></li>
                <li><Link to={`/${siteKey}/pro-rodice`}>Pro rodiče</Link></li>
                <li><Link to={`/${siteKey}/dokumenty?category=formulare`}>Formuláře</Link></li>
                <li><Link to={`/${siteKey}/jidelnicek`}>Jídelníček</Link></li>
              </ul>
            </div>

            {/* Other site link */}
            <div>
              <h3 className="font-bold mb-4 text-[#FECA57]">Navštivte také</h3>
              <Link 
                to={siteKey === 'ms' ? '/ds' : '/ms'}
                className="block p-4 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{siteKey === 'ms' ? '🏡' : '🏫'}</span>
                  <div>
                    <div className="font-bold text-white">
                      {siteKey === 'ms' ? 'Chaloupka na vršku' : 'MŠ Cestička'}
                    </div>
                    <div className="text-xs text-slate-300">
                      {siteKey === 'ms' ? 'Dětská skupina' : 'Mateřská škola'}
                    </div>
                  </div>
                </div>
              </Link>
              
              <div className="mt-6">
                <Link to="/" className="text-sm text-[#FECA57] hover:text-white">
                  ← Zpět na rozcestník
                </Link>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-600 mt-8 pt-6 text-center text-sm text-slate-400">
            <p>© {new Date().getFullYear()} {siteName}. Všechna práva vyhrazena.</p>
            <p className="mt-2">Web vytvořila firma <a href="https://it-produkty.cz" target="_blank" rel="noopener noreferrer" className="text-[#FECA57] hover:text-white transition-colors">IT-Produkty.cz</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
